import { useEffect, useRef, useState } from 'react'
import { CRITERIA_FIELDS } from '../workspace.js'

const LABELS = {
  yearMin: 'Year from',
  yearMax: 'Year to',
  priceMax: 'Max price',
  mileageMax: 'Max mileage',
  mileageSoftMax: 'Soft max mileage',
  distanceMaxMiles: 'Max distance (mi)',
}

// Every editable field except the checkbox, in workspace.js's own order —
// built off CRITERIA_FIELDS rather than a hand-typed list so this can't drift
// from the seven fields the rules and saveCriteria actually accept.
const NUMBER_FIELDS = CRITERIA_FIELDS.filter((f) => f !== 'dealerOnly')

/**
 * Mirrors both the crawler's own sanity check AND the bounds
 * database.rules.json actually enforces on /criteria (yearMin/yearMax
 * 1970-2100, priceMax > 0 and <= 1,000,000, both mileages 0-1,000,000,
 * distanceMaxMiles > 0 and <= 5,000) — read from the rules file, not
 * reconstructed from memory, so this can't quietly drift from what the
 * server actually accepts. Without this, a fat-fingered year like 3000 sails
 * through this form and comes back from Firebase as a bare PERMISSION_DENIED,
 * which tells the operator nothing about what was wrong.
 *
 * Runs on the raw form strings (before numOrNull below), so '' reads the same
 * as "not set" here rather than as 0 — an emptied field imposes no constraint
 * and can't be inverted or out of range.
 */
function validate(v) {
  const num = (key) => Number(v[key])

  if (v.yearMin !== '' && (num('yearMin') < 1970 || num('yearMin') > 2100)) {
    return 'Year from must be between 1970 and 2100.'
  }
  if (v.yearMax !== '' && (num('yearMax') < 1970 || num('yearMax') > 2100)) {
    return 'Year to must be between 1970 and 2100.'
  }
  if (v.yearMin !== '' && v.yearMax !== '' && num('yearMin') > num('yearMax')) {
    return 'Year from must not be after Year to.'
  }
  if (v.priceMax !== '' && num('priceMax') <= 0) return 'Max price must be above 0.'
  if (v.priceMax !== '' && num('priceMax') > 1000000) {
    return 'Max price must be at most 1,000,000.'
  }
  if (v.mileageMax !== '' && (num('mileageMax') < 0 || num('mileageMax') > 1000000)) {
    return 'Max mileage must be between 0 and 1,000,000.'
  }
  if (v.mileageSoftMax !== '' && (num('mileageSoftMax') < 0 || num('mileageSoftMax') > 1000000)) {
    return 'Soft max mileage must be between 0 and 1,000,000.'
  }
  if (v.distanceMaxMiles !== '' && num('distanceMaxMiles') <= 0) {
    return 'Max distance must be above 0.'
  }
  if (v.distanceMaxMiles !== '' && num('distanceMaxMiles') > 5000) {
    return 'Max distance must be at most 5,000.'
  }
  if (v.mileageMax !== '' && v.mileageSoftMax !== '' &&
      num('mileageMax') > num('mileageSoftMax')) {
    return 'Max mileage must not exceed the soft max.'
  }
  return null
}

const stateFrom = (criteria = {}) => ({
  ...Object.fromEntries(NUMBER_FIELDS.map((f) => [f, criteria[f] ?? ''])),
  dealerOnly: criteria.dealerOnly ?? true,
})

// Empty means "no constraint", which workspace.js's saveCriteria only honors
// as an explicit null — it skips '' as "not set" and would leave the old
// value in place. Number(v) turns a genuinely-typed value into a real number
// rather than the string a form input hands back, matching what saveCriteria
// itself does not have to redo, and what its own tests assert (a number, not
// a numeric string, reaches update()).
const numOrNull = (v) => (v === '' ? null : Number(v))

const payloadFrom = (values) => ({
  ...Object.fromEntries(NUMBER_FIELDS.map((f) => [f, numOrNull(values[f])])),
  dealerOnly: values.dealerOnly,
})

// A criteria-shaped object's own field values, in CRITERIA_FIELDS order, for
// comparing the incoming prop against what this form last sent — an absent
// field (undefined, never written) and an explicitly cleared one (null) both
// read as "unset" here, which is all this comparison needs.
const fieldValues = (criteria = {}) => CRITERIA_FIELDS.map((f) => criteria[f] ?? null)
const sameCriteria = (a, b) => {
  const av = fieldValues(a)
  const bv = fieldValues(b)
  return av.every((v, i) => v === bv[i])
}
const canonicalKey = (criteria) => JSON.stringify(fieldValues(criteria))

export default function CriteriaEditor({ criteria = {}, onSave }) {
  const [open, setOpen] = useState(false)
  const [values, setValues] = useState(() => stateFrom(criteria))
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  // Prop-derived draft state, same shape of problem FilterBar.jsx solves and
  // for the same reason: a lazy initializer only ever reads `criteria` once,
  // at mount. This node is read from Firebase (Task 13), so it can easily be
  // `{}` for the first render or two before the listener delivers the real
  // values — without this effect the form would freeze on that empty
  // snapshot and the operator would open "Search settings" to find nothing
  // there, with no way to reload it short of remounting the page. lastSentRef
  // starts at the initial `criteria` prop and is advanced by save() too, so
  // an echo of our own just-written values (the listener firing again after
  // a save) is recognised as "nothing external changed" and does not reseed
  // over whatever the operator may have started typing next. What this does
  // not buy — same caveat as FilterBar's — is telling a stale echo apart from
  // a real external edit made in between; see FilterBar.jsx for the detail.
  const lastSentRef = useRef(criteria)

  useEffect(() => {
    if (sameCriteria(criteria, lastSentRef.current)) return
    lastSentRef.current = criteria
    setValues(stateFrom(criteria))
    setError(null)
    setSaved(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalKey(criteria)])

  const set = (e) => {
    const { name, type, value, checked } = e.target
    setValues((s) => ({ ...s, [name]: type === 'checkbox' ? checked : value }))
    setSaved(false)
  }

  const save = async () => {
    const problem = validate(values)
    setError(problem)
    if (problem) return
    const payload = payloadFrom(values)
    lastSentRef.current = payload
    try {
      await onSave?.(payload)
      setSaved(true)
    } catch {
      // A failed write must not claim success. The caller (App.jsx) already
      // surfaces the failure — its toast is the only claim that belongs on
      // screen, so this panel's own state is left exactly as it was.
    }
  }

  return (
    <section className="criteria">
      {/* The header is a button, so its content model permits phrasing
          content only — no h2 nested inside it (React won't warn, but the
          DOM is invalid and assistive tech gets an unpredictable accessible
          name). Heading semantics come from role="heading" on a span
          instead, same pattern as DealerGroup's panel header. */}
      <button className="section-head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span role="heading" aria-level="2">Search settings</span>
        <span className="chev" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="criteria-body">
          <p className="hint">
            What the crawler matches and alerts on. Takes effect at the next crawl (07:00 CT),
            not immediately.
          </p>
          <div className="filter-row">
            {NUMBER_FIELDS.map((key) => (
              <label key={key}>
                {LABELS[key]}
                <input type="number" name={key} value={values[key]} onChange={set} />
              </label>
            ))}
            <label className="check">
              <input type="checkbox" name="dealerOnly" checked={values.dealerOnly} onChange={set} />
              Dealers only
            </label>
          </div>
          {error && <p className="err">{error}</p>}
          {saved && !error && <p className="muted">Saved. Applies at the next crawl.</p>}
          <button className="primary" onClick={save}>Save criteria</button>
        </div>
      )}
    </section>
  )
}
